import { useEffect, useState, useCallback } from "react";
import { gameApi } from "@/lib/api";
import { toast } from "sonner";
import { HorseArt } from "@/components/game/HorseArt";
import {
  ArrowLeft, MessageSquarePlus, Pin, Lock, Flag, ThumbsUp, ThumbsDown, Heart, Frown,
  Bell, ShieldAlert, Trash2, RotateCcw, Quote, Loader2, Search, Eye, MessageSquare, ChevronLeft,
} from "lucide-react";
import { ForumMod } from "@/components/game/ForumMod";

const REACTIONS = [
  { k: "like", Icon: ThumbsUp, label: "Like" }, { k: "dislike", Icon: ThumbsDown, label: "Dislike" },
  { k: "love", Icon: Heart, label: "Love" }, { k: "sad", Icon: Frown, label: "Sad" },
];
const SORTS = [["latest", "Latest Activity"], ["newest", "Newest"], ["replies", "Most Replies"], ["viewed", "Most Viewed"], ["unanswered", "Unanswered"]];
const FILTERS = [["", "All"], ["mine", "My Threads"], ["following", "Following"], ["pinned", "Pinned"], ["locked", "Locked"]];
const REPORT_REASONS = ["Harassment or bullying", "Hate speech", "Sexual or inappropriate content", "Threats or violence", "Spam", "Scam or impersonation", "Personal information", "Cheating discussion", "Underage safety concern", "Off-topic", "Other"];

const Badge = ({ b }) => {
  const styles = { Admin: "bg-amber-500 text-white", Moderator: "bg-blue-500 text-white", "Founding Player": "bg-purple-500 text-white", Champion: "bg-green-600 text-white" };
  return <span className={`rounded-full px-1.5 py-0.5 text-[9px] font-bold ${styles[b] || "bg-muted text-muted-foreground"}`}>{b}</span>;
};

const NTYPE = { reply: "💬", quote: "❝", moderation: "🛡️", warning: "⚠️", report: "🚩", role: "⭐", appeal: "⚖️" };

const AttachCard = ({ att, small }) => {
  if (!att) return null;
  const meta = att.meta || {};
  const sub = att.kind === "stable" ? `${meta.kept ?? 0} kept · ${meta.completed ?? 0} makeover(s)`
    : att.kind === "result" ? `${meta.placing ? `Placed ${meta.placing}` : "Result"}${meta.overall ? ` · score ${meta.overall}` : ""}`
    : `${meta.hma ? meta.hma + " · " : ""}${meta.day ? `Day ${meta.day}` : "Mustang"}`;
  return (
    <div data-testid="attach-card" className={`mt-2 flex items-center gap-2 rounded-xl border border-secondary/40 bg-secondary/5 p-2 ${small ? "" : ""}`}>
      <div className="grid h-11 w-11 shrink-0 place-items-center overflow-hidden rounded-lg" style={{ background: `${att.color_hex || "#8a8a8a"}22` }}>
        <HorseArt src={att.art_image} color={att.color_hex || "#8a8a8a"} size={44} />
      </div>
      <div className="min-w-0">
        <p className="truncate text-xs font-bold text-primary">{att.kind === "stable" ? "🏡 " : "🐴 "}{att.label || att.horse_name}</p>
        <p className="truncate text-[11px] text-muted-foreground">{att.kind === "stable" ? "Stable" : att.kind === "result" ? "Results Card" : "Horse Profile"} · {sub}</p>
      </div>
    </div>
  );
};

const Author = ({ a, onOpen, size = 34 }) => (
  <button data-testid={`forum-author-${a.player_number}`} onClick={() => a.player_number && onOpen(a.player_number)} className="flex items-center gap-2 text-left">
    <div className="grid shrink-0 place-items-center overflow-hidden rounded-full border border-border" style={{ width: size, height: size, background: `${a.color_hex || "#8a8a8a"}22` }}>
      <HorseArt src={a.thumb} color={a.color_hex || "#8a8a8a"} size={size} />
    </div>
    <div className="min-w-0">
      <div className="flex flex-wrap items-center gap-1">
        <span className="truncate text-xs font-bold text-primary" style={{ color: a.accent_color || undefined }}>{a.display_name}</span>
        {typeof a.level === "number" && <span data-testid="forum-author-level" className="rounded-full bg-emerald-100 px-1.5 py-0.5 text-[9px] font-bold text-emerald-700">Lv {a.level}</span>}
        {(a.badges || []).map((b) => <Badge key={b} b={b} />)}
      </div>
      <span className="text-[10px] text-muted-foreground">#{a.player_number}{a.stable_name ? ` · ${a.stable_name}` : ""}</span>
    </div>
  </button>
);

const ReactBar = ({ data, mine, onReact, testid }) => (
  <div className="flex gap-1.5" data-testid={testid}>
    {REACTIONS.map(({ k, Icon }) => (
      <button key={k} data-testid={`${testid}-${k}`} onClick={() => onReact(k)}
        className={`flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold transition-colors ${mine === k ? "border-secondary bg-secondary/15 text-secondary" : "border-border text-muted-foreground hover:bg-muted"}`}>
        <Icon size={12} /> {data[k] > 0 ? data[k] : ""}
      </button>
    ))}
  </div>
);

export const Forum = ({ onBack, onOpenProfile }) => {
  const [boot, setBoot] = useState(null);
  const [view, setView] = useState("list");
  const [threads, setThreads] = useState(null);
  const [active, setActive] = useState(null); // thread detail {thread, replies, role}
  const [category, setCategory] = useState("");
  const [sort, setSort] = useState("latest");
  const [filter, setFilter] = useState("");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [rulesModal, setRulesModal] = useState(false);
  const [rulesList, setRulesList] = useState([]);
  const [reportFor, setReportFor] = useState(null); // {type,id}
  const [reportReason, setReportReason] = useState(REPORT_REASONS[0]);
  const [reportNote, setReportNote] = useState("");
  const [composer, setComposer] = useState({ category_id: "", title: "", body: "", preview: false });
  const [replyBody, setReplyBody] = useState("");
  const [composerImg, setComposerImg] = useState(null);
  const [replyImg, setReplyImg] = useState(null);
  const [imgBusy, setImgBusy] = useState(false);
  const [quote, setQuote] = useState(null);
  const [notifs, setNotifs] = useState(null);
  const [shareables, setShareables] = useState(null);
  const [attach, setAttach] = useState(null); // {kind, ref_id, label, ...}
  const [sharePicker, setSharePicker] = useState(false);
  const [appealText, setAppealText] = useState("");
  const [appealAck, setAppealAck] = useState(false);
  const [appealOpen, setAppealOpen] = useState(false);

  const loadBoot = useCallback(() => gameApi.get("/game/forum/bootstrap").then((r) => setBoot(r.data)).catch(() => {}), []);
  const loadThreads = useCallback(() => {
    gameApi.get("/game/forum/threads", { params: { category, sort, filter, q } }).then((r) => setThreads(r.data.threads)).catch(() => setThreads([]));
  }, [category, sort, filter, q]);

  useEffect(() => { loadBoot(); }, [loadBoot]);
  useEffect(() => { if (view === "list") loadThreads(); }, [view, loadThreads]);

  const openThread = async (id) => {
    setBusy(true);
    try { const { data } = await gameApi.get(`/game/forum/threads/${id}`); setActive(data); setView("thread"); setQuote(null); setReplyBody(""); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not open thread"); } finally { setBusy(false); }
  };
  const refreshThread = () => active && gameApi.get(`/game/forum/threads/${active.thread.id}`).then((r) => setActive(r.data));

  const ensureRules = async (after) => {
    if (boot?.me?.rules_accepted) { after(); return; }
    const { data } = await gameApi.get("/game/forum/rules");
    setRulesList(data.rules); setRulesModal(after);
  };
  const acceptRules = async () => {
    await gameApi.post("/game/forum/accept-rules");
    await loadBoot();
    const cb = rulesModal; setRulesModal(false);
    if (typeof cb === "function") cb();
  };

  const submitThread = async () => {
    if (composer.title.trim().length < 3 || !composer.category_id || !composer.body.trim()) { toast.error("Add a category, a title, and a message."); return; }
    setBusy(true);
    try {
      const payload = { category_id: composer.category_id, title: composer.title, body: composer.body };
      if (attach) payload.attachment = { kind: attach.kind, ref_id: attach.ref_id };
      if (composerImg) payload.image_url = composerImg;
      const { data } = await gameApi.post("/game/forum/threads", payload);
      toast.success("Thread posted"); setComposer({ category_id: "", title: "", body: "", preview: false }); setAttach(null); setComposerImg(null);
      await loadBoot(); openThread(data.id);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not post"); } finally { setBusy(false); }
  };

  const openShare = async () => {
    if (!shareables) { try { const { data } = await gameApi.get("/game/forum/shareables"); setShareables(data.items); } catch { setShareables([]); } }
    setSharePicker(true);
  };
  const submitAppeal = async () => {
    if (!appealText.trim()) { toast.error("Add a short explanation."); return; }
    try { await gameApi.post("/game/forum/appeal", { explanation: appealText, acknowledge: appealAck });
      toast.success("Appeal submitted — a moderator will review it."); setAppealOpen(false); setAppealText(""); setAppealAck(false); loadBoot(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not submit appeal"); }
  };
  const toggleNotifyPref = async () => {
    try { await gameApi.post("/game/forum/notify-pref", { notify: !me.notify }); loadBoot(); toast.success("Preference saved"); } catch { toast.error("Failed"); }
  };

  const uploadImg = async (file, setter) => {
    if (!file) return;
    if (!file.type?.startsWith("image/")) { toast.error("Please choose an image file."); return; }
    setImgBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await gameApi.post("/game/forum/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setter(data.url); toast.success("Photo added");
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); } finally { setImgBusy(false); }
  };

  const submitReply = async () => {
    if (!replyBody.trim()) return;
    setBusy(true);
    try {
      await gameApi.post(`/game/forum/threads/${active.thread.id}/reply`, { body: replyBody, quote_reply_id: quote?.id || null, image_url: replyImg || null });
      setReplyBody(""); setQuote(null); setReplyImg(null); await refreshThread();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not reply"); } finally { setBusy(false); }
  };

  const react = async (target_type, target_id, reaction) => {
    try { await gameApi.post("/game/forum/react", { target_type, target_id, reaction }); refreshThread(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not react"); }
  };
  const subscribe = async () => {
    const { data } = await gameApi.post(`/game/forum/threads/${active.thread.id}/subscribe`);
    toast.success(data.following ? "Following thread" : "Unfollowed"); refreshThread();
  };
  const deleteOwnThread = async () => {
    if (!window.confirm("Delete this thread?")) return;
    try { await gameApi.delete(`/game/forum/threads/${active.thread.id}`); toast.success("Deleted"); setView("list"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not delete"); }
  };
  const deleteReply = async (rid, isMod) => {
    if (!window.confirm(isMod ? "Remove this reply?" : "Delete your reply?")) return;
    try { await gameApi.delete(`/game/forum/replies/${rid}`); refreshThread(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const modThread = async (action, extra = {}) => {
    try { await gameApi.post(`/game/forum/mod/thread/${active.thread.id}`, { action, ...extra }); toast.success("Done"); refreshThread(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const modReply = async (rid, action) => {
    try { await gameApi.post(`/game/forum/mod/reply/${rid}`, { action }); refreshThread(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const submitReport = async () => {
    try { await gameApi.post("/game/forum/report", { target_type: reportFor.type, target_id: reportFor.id, reason: reportReason, note: reportNote });
      toast.success("Reported — thank you"); setReportFor(null); setReportNote(""); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const openNotifs = async () => { setView("notifications"); const { data } = await gameApi.get("/game/forum/notifications"); setNotifs(data.notifications); await gameApi.post("/game/forum/notifications/read"); loadBoot(); };

  const me = boot?.me;
  const isMod = me?.role === "moderator" || me?.role === "admin";
  const catName = (id) => boot?.categories.find((c) => c.id === id)?.name || "";

  if (!boot) return <div className="grid h-full place-items-center"><Loader2 className="animate-spin text-primary" /></div>;

  // ---- Moderation panel ----
  if (view === "mod") return <ForumMod me={me} onBack={() => { setView("list"); loadBoot(); }} />;

  // ---- Notifications inbox ----
  if (view === "notifications") return (
    <div className="min-h-full bg-background pb-16" data-testid="forum-notifications-view">
      <div className="sticky top-0 z-10 flex items-center gap-2 border-b border-border bg-card px-3 py-3">
        <button data-testid="notif-back" onClick={() => setView("list")} className="rounded-full p-1.5 hover:bg-muted"><ArrowLeft size={18} /></button>
        <h1 className="font-display text-lg font-bold text-primary">Notifications</h1>
        <button data-testid="notif-pref-toggle" onClick={toggleNotifyPref} className={`ml-auto rounded-full border px-2.5 py-1 text-[11px] font-semibold ${me.notify ? "border-secondary text-secondary" : "border-border text-muted-foreground"}`}>
          {me.notify ? "Alerts On" : "Alerts Off"}
        </button>
      </div>
      <div className="px-3 py-3">
        {!notifs ? <div className="grid place-items-center py-10"><Loader2 className="animate-spin text-primary" /></div>
          : notifs.length === 0 ? <p className="py-10 text-center text-sm text-muted-foreground">You're all caught up! 🎉</p>
          : <div className="space-y-2">
              {notifs.map((n) => (
                <button key={n.id} data-testid={`notif-${n.id}`} disabled={!n.ref} onClick={() => n.ref && openThread(n.ref)}
                  className={`flex w-full items-start gap-2 rounded-xl border border-border p-3 text-left ${n.ref ? "bg-card active:scale-[0.99]" : "bg-muted/40"} ${!n.read ? "border-l-4 border-l-secondary" : ""}`}>
                  <span className="text-lg leading-none">{NTYPE[n.type] || "🔔"}</span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-foreground">{n.text}</p>
                    <p className="mt-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">{n.type} · {n.created_at?.slice(0, 16).replace("T", " ")}</p>
                  </div>
                </button>
              ))}
            </div>}
      </div>
    </div>
  );

  return (
    <div className="min-h-full bg-background pb-24" data-testid="forum-view">
      <div className="sticky top-0 z-10 flex items-center gap-2 border-b border-border bg-card/95 px-3 py-3 backdrop-blur">
        <button data-testid="forum-back" onClick={() => (view === "list" ? onBack() : setView("list"))} className="rounded-full p-1.5 hover:bg-muted"><ArrowLeft size={18} /></button>
        <h1 className="font-display text-lg font-bold text-primary">{view === "thread" ? "Thread" : view === "new" ? "New Thread" : "Player Forum"}</h1>
        <div className="ml-auto flex items-center gap-1.5">
          <button data-testid="forum-rules-btn" onClick={async () => { const { data } = await gameApi.get("/game/forum/rules"); setRulesList(data.rules); setRulesModal("view"); }} className="rounded-full p-1.5 text-muted-foreground hover:bg-muted"><ShieldAlert size={17} /></button>
          <button data-testid="forum-notif-btn" onClick={openNotifs} className="relative rounded-full p-1.5 text-muted-foreground hover:bg-muted">
            <Bell size={17} />{me?.unread > 0 && <span className="absolute -right-0.5 -top-0.5 grid h-4 min-w-4 place-items-center rounded-full bg-secondary px-1 text-[9px] font-bold text-white">{me.unread}</span>}
          </button>
          {isMod && <button data-testid="forum-mod-btn" onClick={() => setView("mod")} className="rounded-full bg-amber-500/15 p-1.5 text-amber-600 hover:bg-amber-500/25"><ShieldAlert size={17} /></button>}
        </div>
      </div>

      {/* restriction banner + appeal entry */}
      {me?.restricted && (
        <div className="mx-3 mt-3 rounded-xl border border-destructive/40 bg-destructive/5 p-3" data-testid="forum-restriction-banner">
          <p className="text-xs font-bold text-destructive">Your forum posting is currently restricted{me.silenced_until ? " (silenced)" : me.banned_perm ? " (permanent ban)" : " (banned)"}.</p>
          <p className="mt-0.5 text-[11px] text-muted-foreground">Normal gameplay is unaffected. {me.restriction_reason ? `Reason: ${me.restriction_reason}.` : ""}</p>
          {me.appeal && me.appeal.status === "open"
            ? <p className="mt-1 text-[11px] font-semibold text-secondary" data-testid="appeal-pending">Appeal submitted — under review.</p>
            : me.appeal && me.appeal.status === "resolved"
            ? <p className="mt-1 text-[11px] font-semibold text-secondary">Appeal outcome: {me.appeal.outcome}.</p>
            : <button data-testid="appeal-open-btn" onClick={() => setAppealOpen(true)} className="mt-1.5 rounded-lg bg-destructive px-3 py-1.5 text-[11px] font-bold text-white">Appeal this decision</button>}
        </div>
      )}

      {/* LIST */}
      {view === "list" && (
        <div className="px-3 py-3">
          <div className="mb-2 flex items-center gap-2 rounded-xl border border-border bg-card px-2.5 py-1.5">
            <Search size={15} className="text-muted-foreground" />
            <input data-testid="forum-search" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && loadThreads()} placeholder="Search title, name, #ID, stable, horse…" className="flex-1 bg-transparent text-sm outline-none" />
          </div>
          <div className="mb-2 flex gap-2">
            <select data-testid="forum-sort" value={sort} onChange={(e) => setSort(e.target.value)} className="flex-1 rounded-lg border border-border bg-card px-2 py-1.5 text-xs">{SORTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
            <select data-testid="forum-filter" value={filter} onChange={(e) => setFilter(e.target.value)} className="flex-1 rounded-lg border border-border bg-card px-2 py-1.5 text-xs">{FILTERS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
          </div>
          <div className="mb-3 flex gap-1.5 overflow-x-auto pb-1" data-testid="forum-categories">
            <button onClick={() => setCategory("")} className={`shrink-0 rounded-full px-3 py-1 text-xs font-semibold ${category === "" ? "bg-primary text-primary-foreground" : "bg-card text-muted-foreground border border-border"}`}>All</button>
            {boot.categories.map((c) => (
              <button key={c.id} data-testid={`forum-cat-${c.id}`} onClick={() => setCategory(c.id)} className={`shrink-0 rounded-full px-3 py-1 text-xs font-semibold ${category === c.id ? "bg-primary text-primary-foreground" : "bg-card text-muted-foreground border border-border"}`}>{c.name}</button>
            ))}
          </div>

          {!threads ? <div className="grid place-items-center py-10"><Loader2 className="animate-spin text-primary" /></div>
            : threads.length === 0 ? <p className="py-10 text-center text-sm text-muted-foreground">No threads yet — start the first one!</p>
            : <div className="space-y-2">
                {threads.map((t) => (
                  <button key={t.id} data-testid={`forum-thread-card-${t.id}`} onClick={() => openThread(t.id)} className="block w-full rounded-xl border border-border bg-card p-3 text-left active:scale-[0.99]">
                    <div className="flex items-start gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5">
                          {t.pinned && <Pin size={13} className="shrink-0 text-secondary" data-testid={`pin-${t.id}`} />}
                          {t.locked && <Lock size={13} className="shrink-0 text-muted-foreground" data-testid={`lock-${t.id}`} />}
                          <span className="truncate text-sm font-bold text-primary">{t.title}</span>
                        </div>
                        <p className="mt-0.5 text-[11px] text-muted-foreground">{catName(t.category_id)} · by {t.author.display_name} #{t.author.player_number}{t.attachment ? " · 📎" : ""}</p>
                      </div>
                      <div className="flex shrink-0 flex-col items-end text-[10px] text-muted-foreground">
                        <span className="flex items-center gap-1"><MessageSquare size={11} /> {t.reply_count}</span>
                        <span className="flex items-center gap-1"><Eye size={11} /> {t.view_count}</span>
                      </div>
                    </div>
                  </button>
                ))}
              </div>}
        </div>
      )}

      {/* NEW THREAD */}
      {view === "new" && (
        <div className="px-3 py-3" data-testid="forum-composer">
          <select data-testid="composer-category" value={composer.category_id} onChange={(e) => setComposer({ ...composer, category_id: e.target.value })} className="mb-2 w-full rounded-lg border border-border bg-card px-2 py-2 text-sm">
            <option value="">Choose a category…</option>
            {boot.categories.map((c) => <option key={c.id} value={c.id}>{c.group} · {c.name}</option>)}
          </select>
          <input data-testid="composer-title" value={composer.title} onChange={(e) => setComposer({ ...composer, title: e.target.value })} placeholder="Thread title" maxLength={140} className="mb-2 w-full rounded-lg border border-border bg-card px-3 py-2 text-sm font-semibold" />
          {composer.preview
            ? <div data-testid="composer-preview" className="mb-2 min-h-24 whitespace-pre-wrap rounded-lg border border-secondary/40 bg-secondary/5 p-3 text-sm">{composer.body || "Nothing to preview yet."}</div>
            : <textarea data-testid="composer-body" value={composer.body} onChange={(e) => setComposer({ ...composer, body: e.target.value })} placeholder="Share your thoughts…" rows={7} className="mb-2 w-full rounded-lg border border-border bg-card px-3 py-2 text-sm" />}
          {attach
            ? <div className="mb-2"><AttachCard att={attach} /><button data-testid="composer-attach-remove" onClick={() => setAttach(null)} className="mt-1 text-[11px] font-semibold text-destructive">Remove attachment</button></div>
            : <button data-testid="composer-attach-btn" onClick={openShare} className="mb-2 flex w-full items-center justify-center gap-2 rounded-lg border-2 border-dashed border-border py-2 text-xs font-bold text-muted-foreground">📎 Attach a horse, results card, or stable</button>}
          {composerImg
            ? <div className="mb-2"><img src={`${process.env.REACT_APP_BACKEND_URL}${composerImg}`} alt="upload" className="max-h-52 rounded-lg border border-border" /><button data-testid="composer-image-remove" onClick={() => setComposerImg(null)} className="mt-1 block text-[11px] font-semibold text-destructive">Remove photo</button></div>
            : <label data-testid="composer-image-btn" className="mb-2 flex w-full cursor-pointer items-center justify-center gap-2 rounded-lg border-2 border-dashed border-border py-2 text-xs font-bold text-muted-foreground">📷 {imgBusy ? "Uploading…" : "Add a photo"}<input type="file" accept="image/*" className="hidden" onChange={(e) => uploadImg(e.target.files?.[0], setComposerImg)} /></label>}
          <div className="flex gap-2">
            <button data-testid="composer-preview-toggle" onClick={() => setComposer({ ...composer, preview: !composer.preview })} className="flex-1 rounded-xl border-2 border-border py-2.5 text-sm font-bold text-primary">{composer.preview ? "Edit" : "Preview"}</button>
            <button data-testid="composer-submit" disabled={busy} onClick={() => { if (!composer.category_id) { toast.error("Please pick a category first."); return; } if (composer.title.trim().length < 3) { toast.error("Give your thread a title (3+ characters)."); return; } if (!composer.body.trim()) { toast.error("Write a message before publishing."); return; } ensureRules(submitThread); }} className="flex-[2] rounded-xl bg-secondary py-2.5 text-sm font-bold text-white disabled:opacity-60">Publish Thread</button>
          </div>
        </div>
      )}

      {/* THREAD DETAIL */}
      {view === "thread" && active && (
        <div className="px-3 py-3" data-testid="forum-thread-detail">
          <div className="rounded-xl border border-border bg-card p-3">
            <div className="flex items-start gap-1.5">
              {active.thread.pinned && <Pin size={14} className="mt-1 shrink-0 text-secondary" />}
              {active.thread.locked && <Lock size={14} className="mt-1 shrink-0 text-muted-foreground" />}
              <h2 className="text-base font-bold text-primary" data-testid="thread-title">{active.thread.title}</h2>
            </div>
            <div className="mt-2"><Author a={active.thread.author} onOpen={onOpenProfile} /></div>
            <p className="mt-3 whitespace-pre-wrap text-sm text-foreground" data-testid="thread-body">{active.thread.body}</p>
            {active.thread.image_url && <img src={`${process.env.REACT_APP_BACKEND_URL}${active.thread.image_url}`} alt="attachment" data-testid="thread-image" className="mt-2 max-h-96 rounded-lg border border-border" />}
            {active.thread.attachment && <AttachCard att={active.thread.attachment} />}
            {active.thread.locked && active.thread.lock_reason && <p className="mt-2 rounded-lg bg-muted px-2 py-1 text-[11px] text-muted-foreground">🔒 Locked: {active.thread.lock_reason}</p>}
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <ReactBar data={active.thread.reactions} mine={active.thread.my_reaction} onReact={(k) => react("thread", active.thread.id, k)} testid="thread-react" />
              <button data-testid="thread-subscribe" onClick={subscribe} className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${active.thread.following ? "border-secondary text-secondary" : "border-border text-muted-foreground"}`}>{active.thread.following ? "Following" : "Follow"}</button>
              <button data-testid="thread-report" onClick={() => setReportFor({ type: "thread", id: active.thread.id })} className="rounded-full border border-border px-2 py-0.5 text-[11px] font-semibold text-muted-foreground"><Flag size={11} className="mr-1 inline" />Report</button>
              {active.thread.is_mine && active.thread.reply_count === 0 && <button data-testid="thread-delete-own" onClick={deleteOwnThread} className="rounded-full border border-destructive px-2 py-0.5 text-[11px] font-semibold text-destructive">Delete</button>}
            </div>
            {isMod && (
              <div className="mt-2 flex flex-wrap gap-1.5 border-t border-border pt-2" data-testid="thread-mod-controls">
                <button data-testid="mod-pin" onClick={() => modThread(active.thread.pinned ? "unpin" : "pin")} className="rounded-lg bg-muted px-2 py-1 text-[10px] font-bold">{active.thread.pinned ? "Unpin" : "Pin"}</button>
                <button data-testid="mod-lock" onClick={() => modThread(active.thread.locked ? "unlock" : "lock", active.thread.locked ? {} : { reason: window.prompt("Lock reason (optional)") || "" })} className="rounded-lg bg-muted px-2 py-1 text-[10px] font-bold">{active.thread.locked ? "Unlock" : "Lock"}</button>
                {!active.thread.deleted
                  ? <button data-testid="mod-delete-thread" onClick={() => modThread("delete")} className="rounded-lg bg-destructive px-2 py-1 text-[10px] font-bold text-white">Remove</button>
                  : <button data-testid="mod-restore-thread" onClick={() => modThread("restore")} className="rounded-lg bg-green-600 px-2 py-1 text-[10px] font-bold text-white">Restore</button>}
              </div>
            )}
          </div>

          {/* replies */}
          <div className="mt-3 space-y-2" data-testid="forum-replies">
            {active.replies.map((r) => (
              <div key={r.id} data-testid={`reply-${r.id}`} className={`rounded-xl border border-border bg-card p-3 ${r.deleted ? "opacity-70" : ""}`}>
                <Author a={r.author} onOpen={onOpenProfile} size={28} />
                {r.quote && <div className="mt-2 rounded-lg border-l-2 border-secondary bg-muted/50 px-2 py-1 text-[11px] text-muted-foreground"><b>{r.quote.author_name}:</b> {r.quote.excerpt}</div>}
                <p className={`mt-2 whitespace-pre-wrap text-sm ${r.deleted ? "italic text-muted-foreground" : "text-foreground"}`} data-testid={`reply-body-${r.id}`}>{r.body}</p>
                {r.image_url && <img src={`${process.env.REACT_APP_BACKEND_URL}${r.image_url}`} alt="attachment" data-testid={`reply-image-${r.id}`} className="mt-2 max-h-80 rounded-lg border border-border" />}
                {!r.deleted && (
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <ReactBar data={r.reactions} mine={r.my_reaction} onReact={(k) => react("reply", r.id, k)} testid={`reply-react-${r.id}`} />
                    <button data-testid={`reply-quote-${r.id}`} onClick={() => { setQuote({ id: r.id, author_name: r.author.display_name, excerpt: r.body.slice(0, 100) }); document.getElementById("reply-box")?.focus(); }} className="text-[11px] font-semibold text-muted-foreground"><Quote size={11} className="mr-0.5 inline" />Quote</button>
                    <button data-testid={`reply-report-${r.id}`} onClick={() => setReportFor({ type: "reply", id: r.id })} className="text-[11px] font-semibold text-muted-foreground"><Flag size={11} className="mr-0.5 inline" />Report</button>
                    {r.is_mine && <button data-testid={`reply-delete-${r.id}`} onClick={() => deleteReply(r.id, false)} className="text-[11px] font-semibold text-destructive"><Trash2 size={11} className="mr-0.5 inline" />Delete</button>}
                    {isMod && !r.is_mine && <button data-testid={`mod-delete-reply-${r.id}`} onClick={() => modReply(r.id, "delete")} className="text-[11px] font-semibold text-destructive">Remove</button>}
                  </div>
                )}
                {r.deleted && isMod && <button data-testid={`mod-restore-reply-${r.id}`} onClick={() => modReply(r.id, "restore")} className="mt-1 text-[11px] font-semibold text-green-600"><RotateCcw size={11} className="mr-0.5 inline" />Restore</button>}
              </div>
            ))}
          </div>

          {/* reply composer */}
          {!active.thread.locked || isMod ? (
            <div className="sticky bottom-0 mt-3 rounded-xl border border-border bg-card p-2" data-testid="reply-composer">
              {quote && <div className="mb-1 flex items-center justify-between rounded bg-muted px-2 py-1 text-[11px] text-muted-foreground">Quoting <b>{quote.author_name}</b><button onClick={() => setQuote(null)} className="text-destructive">×</button></div>}
              <textarea id="reply-box" data-testid="reply-input" value={replyBody} onChange={(e) => setReplyBody(e.target.value)} placeholder="Write a reply…" rows={2} className="w-full rounded-lg border border-border bg-background px-2 py-1.5 text-sm" />
              {replyImg
                ? <div className="mt-1 flex items-center gap-2"><img src={`${process.env.REACT_APP_BACKEND_URL}${replyImg}`} alt="upload" className="max-h-32 rounded-lg border border-border" /><button data-testid="reply-image-remove" onClick={() => setReplyImg(null)} className="text-[11px] font-semibold text-destructive">Remove</button></div>
                : <label data-testid="reply-image-btn" className="mt-1 flex cursor-pointer items-center gap-1 text-[11px] font-semibold text-muted-foreground">📷 {imgBusy ? "Uploading…" : "Add photo"}<input type="file" accept="image/*" className="hidden" onChange={(e) => uploadImg(e.target.files?.[0], setReplyImg)} /></label>}
              <button data-testid="reply-submit" disabled={busy || !replyBody.trim()} onClick={() => ensureRules(submitReply)} className="mt-1 h-9 w-full rounded-lg bg-secondary text-sm font-bold text-white disabled:opacity-60">Reply</button>
            </div>
          ) : <p className="mt-3 rounded-xl bg-muted py-3 text-center text-sm text-muted-foreground">🔒 This thread is locked.</p>}
        </div>
      )}

      {/* sticky new-thread button */}
      {view === "list" && (
        <button data-testid="forum-new-thread" onClick={() => { setComposer({ category_id: category || "", title: "", body: "", preview: false }); setView("new"); }}
          className="fixed bottom-20 right-4 z-20 flex items-center gap-2 rounded-full bg-secondary px-4 py-3 font-bold text-white shadow-lg active:scale-95">
          <MessageSquarePlus size={18} /> New
        </button>
      )}

      {/* share picker */}
      {sharePicker && (
        <div className="fixed inset-0 z-[95] flex items-end justify-center bg-black/50 p-4 sm:items-center" data-testid="forum-share-picker" onClick={() => setSharePicker(false)}>
          <div className="max-h-[75vh] w-full max-w-md overflow-y-auto rounded-2xl border border-border bg-card p-5" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display text-lg font-bold text-primary">Attach to your post</h3>
            <p className="mt-1 text-[11px] text-muted-foreground">Share one of your own horses, results, or your stable.</p>
            {!shareables ? <div className="grid place-items-center py-6"><Loader2 className="animate-spin text-primary" /></div>
              : shareables.length === 0 ? <p className="py-6 text-center text-sm text-muted-foreground">Nothing to share yet — play a makeover first!</p>
              : <div className="mt-3 space-y-2">
                  {shareables.map((it, i) => (
                    <button key={`${it.kind}-${it.ref_id}-${i}`} data-testid={`share-item-${it.kind}-${i}`} onClick={() => { setAttach(it); setSharePicker(false); }} className="block w-full text-left">
                      <AttachCard att={it} />
                    </button>
                  ))}
                </div>}
            <button data-testid="share-cancel" onClick={() => setSharePicker(false)} className="mt-3 h-10 w-full rounded-xl border-2 border-border font-bold text-primary">Cancel</button>
          </div>
        </div>
      )}

      {/* appeal modal */}
      {appealOpen && (
        <div className="fixed inset-0 z-[95] flex items-end justify-center bg-black/50 p-4 sm:items-center" data-testid="forum-appeal-modal">
          <div className="w-full max-w-md rounded-2xl border border-border bg-card p-5">
            <h3 className="font-display text-lg font-bold text-primary">Appeal a moderation decision</h3>
            <p className="mt-1 text-[11px] text-muted-foreground">You may submit one appeal. A moderator or admin will review it.</p>
            <textarea data-testid="appeal-text" value={appealText} onChange={(e) => setAppealText(e.target.value)} placeholder="Explain your side…" rows={4} className="mt-3 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" />
            <label className="mt-2 flex items-center gap-2 text-[11px] font-semibold text-muted-foreground">
              <input type="checkbox" data-testid="appeal-ack" checked={appealAck} onChange={(e) => setAppealAck(e.target.checked)} /> I understand the forum rules.
            </label>
            <div className="mt-3 flex gap-2">
              <button data-testid="appeal-cancel" onClick={() => setAppealOpen(false)} className="flex-1 rounded-xl border-2 border-border py-2.5 font-bold text-primary">Cancel</button>
              <button data-testid="appeal-submit" onClick={submitAppeal} className="flex-1 rounded-xl bg-secondary py-2.5 font-bold text-white">Submit Appeal</button>
            </div>
          </div>
        </div>
      )}

      {/* rules modal */}
      {rulesModal && (
        <div className="fixed inset-0 z-[95] flex items-end justify-center bg-black/50 p-4 sm:items-center" data-testid="forum-rules-modal">
          <div className="max-h-[80vh] w-full max-w-md overflow-y-auto rounded-2xl border border-border bg-card p-5">
            <h3 className="font-display text-lg font-bold text-primary">Forum Rules</h3>
            <ul className="mt-3 space-y-2 text-sm text-foreground">{rulesList.map((r, i) => <li key={i} className="flex gap-2"><span className="text-secondary">•</span>{r}</li>)}</ul>
            {rulesModal === "view"
              ? <button data-testid="rules-close" onClick={() => setRulesModal(false)} className="mt-4 h-11 w-full rounded-xl border-2 border-border font-bold text-primary">Close</button>
              : <button data-testid="rules-accept" onClick={acceptRules} className="mt-4 h-11 w-full rounded-xl bg-secondary font-bold text-white">I Accept — Let me post</button>}
          </div>
        </div>
      )}

      {/* report modal */}
      {reportFor && (
        <div className="fixed inset-0 z-[95] flex items-end justify-center bg-black/50 p-4 sm:items-center" data-testid="forum-report-modal">
          <div className="w-full max-w-md rounded-2xl border border-border bg-card p-5">
            <h3 className="font-display text-lg font-bold text-primary">Report content</h3>
            <select data-testid="report-reason" value={reportReason} onChange={(e) => setReportReason(e.target.value)} className="mt-3 w-full rounded-lg border border-border bg-background px-2 py-2 text-sm">{REPORT_REASONS.map((r) => <option key={r}>{r}</option>)}</select>
            <textarea data-testid="report-note" value={reportNote} onChange={(e) => setReportNote(e.target.value)} placeholder="Optional details" rows={2} className="mt-2 w-full rounded-lg border border-border bg-background px-2 py-1.5 text-sm" />
            <div className="mt-3 flex gap-2">
              <button data-testid="report-cancel" onClick={() => setReportFor(null)} className="flex-1 rounded-xl border-2 border-border py-2.5 font-bold text-primary">Cancel</button>
              <button data-testid="report-submit" onClick={submitReport} className="flex-1 rounded-xl bg-destructive py-2.5 font-bold text-white">Submit</button>
            </div>
          </div>
        </div>
      )}

      {/* rules modal END */}
    </div>
  );
};
